import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AvatarEditor from "react-avatar-editor";
import { uploadBytesResumable, ref as storageRef, getDownloadURL } from "firebase/storage";
import { db, storage } from "../services/firebase";
import { setDoc, doc, collection } from "firebase/firestore";
import { nanoid } from "nanoid";
import withAuthGuard from "../hoc/withAuthGuard";
import { useAuth } from "../contexts/AuthContext";
import ImageUploader from "../components/ImageUploader";
import GoodsForm from "../components/GoodsForm";
import AvatarEditorComponent from "../components/AvatarEditorComponent";
import { trimImage, resizeImage } from "../utils/imageUtils";
import HeightInputComponent from "../components/HeightInputComponent";

/**
 * BlobからHTMLImageElementを生成するヘルパー関数
 * @param blob 画像のBlob
 * @returns Promise<HTMLImageElement>
 */
const createImage = (blob: Blob): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("画像の読み込みに失敗しました"));
    };
    img.src = url;
  });
};

// スタイル定義
const styles = {
  titleContainer: {
    display: "flex",
    alignItems: "center",
    gap: 2,
    mb: 3,
    position: "relative",
    minHeight: 40, // 戻るボタンの高さに合わせる
  },
  backButton: {
    minWidth: 'auto',
    padding: '4px 8px',
  },
};

const ARGoodsRegistrationPage: React.FC = () => {
  const { userUid } = useAuth();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [step, setStep] = useState(1);
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [lightFile, setLightFile] = useState<File | null>(null);
  const [imageUrlLight, setImageUrlLight] = useState<string | null>(null);
  const [scale, setScale] = useState<number>(1.0);
  const editorRef = useRef<AvatarEditor>(null);
  const [goodsName, setGoodsName] = useState("");
  const [description, setDescription] = useState("");
  const [height, setHeight] = useState<number | "">("");

  const handleError = (message: string, error?: any) => {
    console.error(message, error || "");
    alert(message);
  };

  /**
   * Firebase Storageへのアップロード関数
   * @param file アップロードするファイル
   * @param path アップロード先のパス
   * @returns Promise<string> ダウンロードURL
   */
  const uploadFile = async (file: File, path: string): Promise<string> => {
    const storageReference = storageRef(storage, path);
    const uploadTask = uploadBytesResumable(storageReference, file);
    return new Promise<string>((resolve, reject) => {
      uploadTask.on(
        "state_changed",
        null,
        (error) => reject(error),
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(url);
        }
      );
    });
  };

  const handleImageChange = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      handleError("画像形式を選択してください (JPG, PNG, etc.)");
      return;
    }

    try {
      const img = await createImage(file);

      // 1. オリジナル形式でトリミング
      const trimmedBlobOriginal = await trimImage(img, "image/png", 1.0);
      const trimmedFileOriginal = new File([trimmedBlobOriginal], file.name, { type: "image/png" });
      setOriginalFile(trimmedFileOriginal);

      // 2. Light画像のトリミングとリサイズ
      const trimmedBlobWebpLight = await trimImage(img, "image/webp", 0.8);
      const resizedImgWebpLight = await createImage(trimmedBlobWebpLight);
      const resizedBlobWebpLight = await resizeImage(resizedImgWebpLight, 1300, "image/webp", 0.8);
      const lightFileFinal = new File([resizedBlobWebpLight], file.name.replace(/\.\w+$/, "-light.webp"), { type: "image/webp" });
      setLightFile(lightFileFinal);

      // 3. オリジナル画像のURL生成
      const trimmedUrlOriginal = URL.createObjectURL(trimmedBlobOriginal);
      setImageUrl(trimmedUrlOriginal);

      // 4. Light画像のURL生成
      const trimmedUrlLight = URL.createObjectURL(resizedBlobWebpLight);
      setImageUrlLight(trimmedUrlLight);

      // ステップ2に進む
      setStep(2);
    } catch (error) {
      handleError("画像のトリミングに失敗しました。", error);
    }
  };

  const handleSubmitGoodsData = async () => {
    if (!originalFile || !lightFile) {
      handleError("画像ファイルが正しく処理されていません。");
      return;
    }

    try {
      // goodsUIDはドキュメントIDとして使用
      const goodsUID = nanoid(20);
      // URLで使用する別のIDを生成
      const goodsUrlId = nanoid(20);

      // オリジナル画像をFirebase Storageにアップロード (PNG)
      const originalFileName = `${goodsUID}-original.png`;
      const originalPath = `goods-images/${originalFileName}`;
      const originalUrl = await uploadFile(originalFile, originalPath);

      // Light画像をFirebase Storageにアップロード (WebP)
      const lightFileName = `${goodsUID}-light.webp`;
      const lightPath = `goods-images/${lightFileName}`;
      const lightUrl = await uploadFile(lightFile, lightPath);

      // 正方形画像のアップロード
      if (editorRef.current) {
        const canvas = editorRef.current.getImageScaledToCanvas();
        canvas.toBlob(async (blob) => {
          if (blob) {
            const squareFile = new File([blob], `${goodsUID}-square.webp`, { type: "image/webp" });
            const squarePath = `goods-images/${goodsUID}-square.webp`;
            const squareUrl = await uploadFile(squareFile, squarePath);

            // Firestoreにデータを登録（goodsUIDをドキュメントIDとして使用）
            await setDoc(doc(db, "goods", goodsUID), {
              creatorUID: userUid,
              goodsUrlId: goodsUrlId, // 新しく追加
              title: goodsName || "",
              description: description || "",
              height: height || null,
              imageUrl: originalUrl || null,
              imageUrlLight: lightUrl || null,
              imageUrlSquare: squareUrl || null,
              status: "sample",
              totalPhotoCount: 0,
              createdAt: new Date(),  // Timestamp型に変更
              updatedAt: new Date()   // Timestamp型に変更
            });

            alert("グッズが登録されました");
            setStep(5);
          } else {
            handleError("正方形画像の作成に失敗しました。");
          }
        }, "image/webp", 0.8);
      } else {
        handleError("正方形画像の作成に失敗しました。Editorが見つかりません。");
      }
    } catch (error) {
      handleError("グッズ登録中にエラーが発生しました", error);
    }
  };

  // 前のステップに戻る関数
  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  return (
    <Paper elevation={3} sx={{ padding: 4, maxWidth: 800, margin: "auto", position: "relative" }}>
      <Box sx={styles.titleContainer}>
        {step > 1 && (
          <Button
            variant="outlined"
            onClick={handleBack}
            sx={styles.backButton}
          >
            戻る
          </Button>
        )}
        <Typography variant="h5" component="h1">
          {step === 1 && "画像登録"}
          {step === 2 && "グッズ情報入力"}
          {step === 3 && "商品の高さ入力"}
          {step === 4 && "正方形画像の作成"}
          {step === 5 && "登録完了"}
        </Typography>
      </Box>

      {step === 1 && (
        <ImageUploader onImageSelect={handleImageChange} />
      )}

      {step === 2 && originalFile && lightFile && imageUrl && imageUrlLight && (
        <GoodsForm
          goodsName={goodsName}
          description={description}
          height={height}
          imageUrl={imageUrl}
          onGoodsNameChange={setGoodsName}
          onDescriptionChange={setDescription}
          onHeightChange={setHeight}
          onNextStep={() => setStep(3)}
          hideHeight
        />
      )}

      {step === 3 && imageUrl && (
        <>
          <HeightInputComponent
            imageUrl={imageUrl}
            height={height}
            onHeightChange={setHeight}
            onNextStep={() => setStep(4)}
          />
        </>
      )}

      {step === 4 && originalFile && lightFile && imageUrlLight && (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              padding: isSmallScreen ? "0 10px" : "0",
            }}
          >
            <AvatarEditorComponent
              ref={editorRef}
              image={lightFile}
              scale={scale}
              onScaleChange={setScale}
              onSubmit={handleSubmitGoodsData}
            />
          </Box>
        </>
      )}

      {step === 5 && (
        <Typography>グッズの登録が完了しました！</Typography>
      )}
    </Paper>
  );
};

export default withAuthGuard(ARGoodsRegistrationPage);
